// vibe-coded: yes — model: OpenAI o4-mini — tool: Codex CLI apply_patch
'use client';

import { apply } from '@directus/visual-editing';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

function inIframe() {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function visualEditingEnabled(searchParams: URLSearchParams) {
  const qe = (searchParams.get('visual-editing') || '').toLowerCase();
  return inIframe() || qe === '1' || qe === 'true';
}

export default function DirectusVisualEditingClientInit() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const cleanupRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    const directusUrl = process.env.NEXT_PUBLIC_DIRECTUS_URL;
    if (!directusUrl) return;
    if (!visualEditingEnabled(searchParams)) return;

    let cancelled = false;
    (async () => {
      try {
        // Remove any previous overlays before (re)applying on navigation
        if (cleanupRef.current) cleanupRef.current();
        const applyResult = await apply({
          directusUrl,
          onSaved: () => router.refresh(),
        });
        if (typeof applyResult === 'undefined') {
          console.warn(
            'Directus visual editing could not be applied. Check console for errors'
          );
          return;
        }
        const { remove } = applyResult;
        if (!cancelled) cleanupRef.current = remove;
      } catch (err) {
        console.warn('Directus visual editing init failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Re-run on navigation so overlays match the current DOM
  }, [pathname, searchParams, router]);

  // Global cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) void cleanupRef.current();
    };
  }, []);

  return null;
}
